import {
  convertWeight,
  describeSet,
  distanceUnit,
  estimateOneRepMax,
  ExerciseSessions,
  formatDuration,
  markPrs,
  metresPer,
  roundWeight,
  type Exercise,
  type SetRecord,
  type User,
} from '@squats/shared';
import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useCachedApi } from '../cache.ts';
import { formatDayShort, formatMonthDay } from '../dates.ts';
import { PersonPicker, usePeople } from './People.tsx';
import { PrBadge } from './PrBadge.tsx';
import { TrendChart, type Point } from './TrendChart.tsx';

type Dated = { set: SetRecord; date: string };

/** Someone's record with this exercise: their bests, a trend over time, and every session. Anyone in the group can look. */
export function ExerciseHistory({ user, exercise }: { user: User; exercise: Exercise }) {
  const people = usePeople(user);
  const [who, setWho] = useState(user.id);
  const { data, error } = useCachedApi(user.id, `/api/workouts/by-exercise/${exercise.id}?user=${who}`, ExerciseSessions);
  const [showAll, setShowAll] = useState(false);
  const sessions = data?.userId === who ? data.sessions : undefined;

  const all = useMemo(() => (sessions ?? []).flatMap((s) => s.sets.map((set) => ({ set, date: s.date }))), [sessions]);
  const prs = useMemo(
    () =>
      markPrs(
        exercise.type,
        all.map((d) => d.set),
      ),
    [exercise.type, all],
  );
  const name = who === user.id ? 'You' : (people.find((p) => p.id === who)?.displayName ?? 'They');

  return (
    <section className="card stack" aria-labelledby="history-h">
      <div className="row wrap">
        <h2 id="history-h" className="grow">
          History
        </h2>
        <PersonPicker user={user} people={people} value={who} onChange={setWho} label="Whose history" />
      </div>
      {error && !sessions && <p className="muted">{error}</p>}
      {sessions && !sessions.length && <p className="muted">{name === 'You' ? 'You haven’t' : `${name} hasn’t`} logged this yet.</p>}
      {sessions && sessions.length > 0 && (
        <>
          <Records exercise={exercise} sets={all} units={user.units} />
          <TrendChart points={trend(exercise, sessions, user.units)} label={trendLabel(exercise, user.units)} format={(v) => formatTrend(exercise, v, user.units)} />
          <p className="muted small center">{trendLabel(exercise, user.units)}, per session</p>
          <ul className="list">
            {(showAll ? sessions : sessions.slice(0, 8)).map((s) => (
              <li key={s.workoutId} className="list-row session-row">
                <Link href={`/workouts/${s.workoutId}`} className="session-date">
                  {formatDayShort(s.date)}
                </Link>
                <span className="grow session-sets">
                  {s.sets
                    .filter((x) => !x.warmup)
                    .map((x) => (
                      <span key={x.id} className="session-set">
                        {describeSet(exercise.type, x, user.units)}
                        <PrBadge kinds={prs.get(x.id)} />
                      </span>
                    ))}
                </span>
              </li>
            ))}
          </ul>
          {sessions.length > 8 && !showAll && (
            <button type="button" className="btn small" onClick={() => setShowAll(true)}>
              Show all {sessions.length} sessions
            </button>
          )}
        </>
      )}
    </section>
  );
}

function Records({ exercise, sets, units }: { exercise: Exercise; sets: Dated[]; units: User['units'] }) {
  const working = sets.filter((d) => !d.set.warmup);
  const best = (score: (s: SetRecord) => number | null) => {
    let top: (Dated & { score: number }) | null = null;
    for (const d of working) {
      const v = score(d.set);
      // Ties go to the earliest time it was done.
      if (v !== null && v > 0 && (!top || v > top.score || (v === top.score && d.set.loggedAt < top.set.loggedAt))) top = { ...d, score: v };
    }
    return top;
  };
  const kg = (s: SetRecord) => (s.weight === null ? null : convertWeight(s.weight, s.unit, 'kg'));
  const w = (k: number) => `${roundWeight(convertWeight(k, 'kg', units), units)} ${units}`;
  const rows: { label: string; text: string; detail?: string; date: string }[] = [];
  const add = (label: string, d: (Dated & { score: number }) | null, text: (d: Dated & { score: number }) => string, detail?: (d: Dated & { score: number }) => string) =>
    d && rows.push({ label, text: text(d), detail: detail?.(d), date: d.date });

  if (exercise.type === 'weight_reps') {
    add('Heaviest', best(kg), (d) => describeSet('weight_reps', d.set, units));
    add(
      'Best est. 1RM',
      best((s) => (kg(s) === null || !s.reps ? null : estimateOneRepMax(kg(s)!, s.reps))),
      (d) => w(d.score),
      (d) => `from ${describeSet('weight_reps', d.set, units)}`,
    );
  } else if (exercise.type === 'bodyweight_reps') {
    add(
      'Most reps',
      best((s) => s.reps),
      (d) => `${d.score} reps`,
    );
  } else {
    add(
      'Longest',
      best((s) => s.durationS),
      (d) => describeSet(exercise.type, d.set, units),
    );
    if (exercise.type === 'cardio')
      add(
        'Farthest',
        best((s) => s.distanceM),
        (d) => describeSet('cardio', d.set, units),
      );
  }
  if (!rows.length) return null;
  return (
    <dl className="facts records">
      {rows.map((r) => (
        <div key={r.label} className="record">
          <dt>{r.label}</dt>
          <dd>
            <strong>{r.text}</strong>
            <span className="muted small">
              {r.detail && ` ${r.detail} ·`} {formatMonthDay(r.date)}
            </span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** One number per session for the chart: best est. 1RM, most reps, longest hold, or farthest (else longest) cardio. */
function trend(exercise: Exercise, sessions: ExerciseSessions['sessions'], units: User['units']): Point[] {
  const value = (s: SetRecord): number | null => {
    switch (exercise.type) {
      case 'weight_reps':
        return s.weight === null || !s.reps ? null : estimateOneRepMax(convertWeight(s.weight, s.unit, units), s.reps);
      case 'bodyweight_reps':
        return s.reps;
      case 'hold':
        return s.durationS;
      case 'cardio':
        return s.distanceM !== null ? s.distanceM / metresPer(units) : s.durationS !== null ? s.durationS / 60 : null;
    }
  };
  // Sessions come newest first; a day with two sessions keeps its best.
  const byDate = new Map<string, number>();
  for (const s of sessions) {
    const vals = s.sets
      .filter((x) => !x.warmup)
      .map(value)
      .filter((v): v is number => v !== null && v > 0);
    if (vals.length) byDate.set(s.date, Math.max(byDate.get(s.date) ?? 0, ...vals));
  }
  return [...byDate].map(([date, v]) => ({ date, value: v })).sort((a, b) => a.date.localeCompare(b.date));
}

const trendLabel = (exercise: Exercise, units: User['units']) =>
  ({ weight_reps: 'Best est. 1RM', bodyweight_reps: 'Most reps in a set', hold: 'Longest hold', cardio: `Distance (${distanceUnit(units)}) or minutes` })[exercise.type];

function formatTrend(exercise: Exercise, v: number, units: User['units']) {
  if (exercise.type === 'weight_reps') return `${Math.round(v)} ${units}`;
  if (exercise.type === 'hold') return formatDuration(v);
  if (exercise.type === 'cardio') return `${+v.toFixed(1)}`;
  return String(v);
}
